import * as SelectPrimitive from "@radix-ui/react-select";
import {
  addMonths,
  endOfMonth,
  format,
  isSameDay,
  isValid,
  parse,
  setMonth,
  setYear,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import { inputChassis } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useDialogGuard } from "@/hooks/use-dialog-guard";
import { useT } from "@/lib/i18n/use-t";
import { useRegionalFormat } from "@/lib/regional-format/context";
import { cn } from "@/lib/utils";
import { T } from "./date-picker.i18n";

const DATE_FORMAT = "yyyy-MM-dd";

// QL-337 — the year dropdown spans this range. Dates of birth are the far end
// of what the picker is asked for; anything outside is still typeable.
const MIN_YEAR = 1900;
const MAX_YEAR_AHEAD = 20;

function parseValue(value: string | undefined | null): Date | null {
  if (!value) return null;
  const d = parse(value, DATE_FORMAT, new Date());
  return isValid(d) ? d : null;
}

export type DatePickerProps = {
  value?: string | null;
  onChange: (value: string) => void;
  id?: string;
  name?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  required?: boolean;
  "aria-describedby"?: string;
};

export function DatePicker({
  value,
  onChange: onChangeProp,
  id,
  name,
  placeholder,
  disabled = false,
  className,
  required,
  "aria-describedby": ariaDescribedBy,
}: DatePickerProps) {
  const t = useT(T);
  const { regionalFormat, formatDate: formatRegionalDate } = useRegionalFormat();
  const { markDirty } = useDialogGuard();

  // Typing into the native date input fires `change`, but picking a day from
  // the popover is a bare button — invisible until it reports itself here.
  const onChange = React.useCallback(
    (next: string) => {
      markDirty();
      onChangeProp(next);
    },
    [markDirty, onChangeProp],
  );

  const [open, setOpen] = React.useState(false);
  const selected = React.useMemo(() => parseValue(value), [value]);

  // QL-108 — the visible label follows the tenant date format while the
  // stored/sent value stays `yyyy-MM-dd`. The text input shows the formatted
  // value; while the user types, `draft` holds the raw text and a complete
  // date in the tenant pattern commits immediately.
  const [draft, setDraft] = React.useState<string | null>(null);
  const displayValue = draft ?? (value ? formatRegionalDate(value) : "");

  const handleTextChange = (text: string, input: HTMLInputElement) => {
    setDraft(text);
    if (text.trim() === "") {
      input.setCustomValidity("");
      onChange("");
      return;
    }
    const parsed = parse(text.trim(), regionalFormat.dateFormat, new Date());
    // Round-trip guard: only commit input that IS a complete date in the
    // tenant pattern (rejects partial input like "1.2.20" while typing).
    if (isValid(parsed) && format(parsed, regionalFormat.dateFormat) === text.trim()) {
      input.setCustomValidity("");
      onChange(format(parsed, DATE_FORMAT));
    } else {
      // An incomplete draft must not satisfy native `required` validation.
      input.setCustomValidity(`${t.invalidDate} (${regionalFormat.dateFormat})`);
    }
  };

  const handleTextBlur = (input: HTMLInputElement) => {
    // Revert the visible text to the canonical formatted value.
    input.setCustomValidity("");
    setDraft(null);
  };

  // Month being viewed in the calendar grid
  const [viewMonth, setViewMonth] = React.useState<Date>(() => selected ?? new Date());

  // Snap the visible month to whatever the controlled value points at — but only
  // when the value itself changes, not on every render (selected is a fresh Date).
  React.useEffect(() => {
    if (selected) setViewMonth(selected);
  }, [selected]);

  const handleSelectDay = (day: Date) => {
    setDraft(null);
    onChange(format(day, DATE_FORMAT));
    setOpen(false);
  };

  const handleToday = () => {
    setDraft(null);
    const today = new Date();
    onChange(format(today, DATE_FORMAT));
    setViewMonth(today);
    setOpen(false);
  };

  const handleClear = () => {
    setDraft(null);
    onChange("");
    setOpen(false);
  };

  // QL-337 — month and year are dropdowns in the header, so a date of birth
  // decades back is year → month → day instead of stepping months. The list
  // always contains the viewed year, so a value outside the range still shows.
  const viewYear = viewMonth.getFullYear();
  const years = React.useMemo(() => {
    const first = Math.min(MIN_YEAR, viewYear);
    const last = Math.max(new Date().getFullYear() + MAX_YEAR_AHEAD, viewYear);
    return Array.from({ length: last - first + 1 }, (_, i) => first + i);
  }, [viewYear]);

  // Build the day grid (6 rows × 7 cols), Monday week start
  const gridStart = startOfWeek(startOfMonth(viewMonth), { weekStartsOn: 1 });
  const days: Date[] = [];
  for (let i = 0; i < 42; i++) {
    days.push(new Date(gridStart.getTime() + i * 24 * 60 * 60 * 1000));
  }

  const monthStart = startOfMonth(viewMonth);
  const monthEnd = endOfMonth(viewMonth);
  const today = new Date();

  return (
    <div className={cn("relative flex w-full items-center", className)}>
      <input
        id={id}
        name={name}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        value={displayValue}
        onChange={(e) => handleTextChange(e.target.value, e.currentTarget)}
        onBlur={(e) => handleTextBlur(e.currentTarget)}
        disabled={disabled}
        required={required}
        aria-describedby={ariaDescribedBy}
        placeholder={placeholder || regionalFormat.dateFormat}
        className={cn(inputChassis, "pl-8")}
      />
      <Popover open={open} onOpenChange={setOpen}>
        {/* The leading icon is the calendar's handle. Design 14e gives the date
            field one 16px mark and no trailing chevron — typing stays the
            primary path, and the popover is the accelerator hanging off it. */}
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            aria-label={t.openCalendar}
            aria-haspopup="dialog"
            aria-expanded={open}
            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
          >
            <CalendarIcon className="h-4 w-4" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[280px] p-0"
          align="start"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <div className="p-3">
            <div className="mb-3 flex items-center justify-between">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => setViewMonth((m) => addMonths(m, -1))}
                tooltip={t.prevMonth}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              {/* Plain Radix roots rather than the repo's `Select` wrapper: moving
                  the viewed month is navigation, not a change, so it must not
                  mark the enclosing dialog dirty (QL-181). */}
              <div className="flex min-w-0 flex-1 items-center justify-center gap-1 px-1">
                <SelectPrimitive.Root
                  value={String(viewMonth.getMonth())}
                  onValueChange={(v) => setViewMonth((m) => setMonth(m, Number(v)))}
                >
                  <SelectTrigger
                    aria-label={t.chooseMonth}
                    className="h-7 w-auto min-w-0 gap-1 border-transparent bg-transparent py-0 pl-2 pr-1 text-sm font-semibold shadow-none hover:bg-accent"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent align="center">
                    {t.months.map((month, index) => (
                      <SelectItem key={month} value={String(index)}>
                        {month}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </SelectPrimitive.Root>
                <SelectPrimitive.Root
                  value={String(viewYear)}
                  onValueChange={(v) => setViewMonth((m) => setYear(m, Number(v)))}
                >
                  <SelectTrigger
                    aria-label={t.chooseYear}
                    className="h-7 w-auto min-w-0 gap-1 border-transparent bg-transparent py-0 pl-2 pr-1 text-sm font-semibold shadow-none hover:bg-accent"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent align="center">
                    {years.map((year) => (
                      <SelectItem key={year} value={String(year)}>
                        {year}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </SelectPrimitive.Root>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => setViewMonth((m) => addMonths(m, 1))}
                tooltip={t.nextMonth}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            <div className="mb-1 grid grid-cols-7 gap-0.5">
              {t.weekdaysShort.map((wd) => (
                <div key={wd} className="text-center text-[11px] font-medium text-muted-foreground">
                  {wd}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-0.5">
              {days.map((day) => {
                const isCurrentMonth = day >= monthStart && day <= monthEnd;
                const isSelected = selected ? isSameDay(day, selected) : false;
                const isToday = isSameDay(day, today);
                return (
                  <Button
                    key={day.toISOString()}
                    type="button"
                    variant={isSelected ? "default" : "ghost"}
                    size="sm"
                    onClick={() => handleSelectDay(day)}
                    className={cn(
                      "h-8 w-8 p-0 text-xs font-normal",
                      !isCurrentMonth && "opacity-50",
                      isToday && !isSelected && "border border-primary",
                    )}
                  >
                    {day.getDate()}
                  </Button>
                );
              })}
            </div>
            <div className="mt-3 flex gap-2 border-t pt-3">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="flex-1"
                onClick={handleToday}
              >
                {t.today}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="flex-1"
                onClick={handleClear}
              >
                {t.clear}
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
